import React, { useState, useCallback } from 'react';
import { Box, Grid, Typography, LinearProgress } from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import SourcePanel from './SourcePanel';
import MiddlePanel from './MiddlePanel';
import TargetPanel from './TargetPanel';
import DirectMappingDialog from './dialogs/DirectMappingDialog';
import JoinWithAggregateDialog from './dialogs/JoinWithAggregateDialog';
import AggregateMappingDialog from './dialogs/AggregateMappingDialog';
import ConcatMappingDialog from './dialogs/ConcatMappingDialog';
import ComputeMappingDialog from './dialogs/ComputeMappingDialog';

/** Parse "table.column" into { table, column } */
function parseTableCol(str) {
  if (!str || typeof str !== 'string') return { table: '', column: '' };
  const dot = str.indexOf('.');
  if (dot >= 0) return { table: str.slice(0, dot), column: str.slice(dot + 1) };
  return { table: str, column: '' };
}

const ModalContent = ({
  sources,
  mappings,
  setMappings,
  aiSuggestions,
  targetSchema,
  fetchedHeader,
  sourceProfileData,
  targetProfileData,
  disableForm,
  isInitialized,
  duckDBLoading,
  mappingLoading = false,
  totalTargetColumns = 0,
  existingMappings = [],
  onSourcesChange,
  onJoinsChange,
  onAiSuggestionsChange,
  onMappingsChange,
  onRequestAI,
  onSaveDirectMapping,
  onSaveJoinMapping,
  onDeleteMapping,
  onPreview,
  isPreviewReady,
  onSaveMappings,
  onRegenerateMappings,
  onOpenPreviewDialog,
}) => {
  const [directMappingDialogOpen, setDirectMappingDialogOpen] = useState(false);
  const [directMappingInitialValues, setDirectMappingInitialValues] = useState(null);
  const [directMappingEditIndex, setDirectMappingEditIndex] = useState(null);
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [joinDialogInitialValues, setJoinDialogInitialValues] = useState(null);
  const [joinMappingEditIndex, setJoinMappingEditIndex] = useState(null);
  const [aggregateMappingDialogOpen, setAggregateMappingDialogOpen] = useState(false);
  const [aggregateMappingInitialValues, setAggregateMappingInitialValues] = useState(null);
  const [concatMappingDialogOpen, setConcatMappingDialogOpen] = useState(false);
  const [concatMappingInitialValues, setConcatMappingInitialValues] = useState(null);
  const [computeMappingDialogOpen, setComputeMappingDialogOpen] = useState(false);
  const [computeMappingInitialValues, setComputeMappingInitialValues] = useState(null);
  const [concatMappingEditIndex, setConcatMappingEditIndex] = useState(null);
  const [computeMappingEditIndex, setComputeMappingEditIndex] = useState(null);
  const [aggregateMappingEditIndex, setAggregateMappingEditIndex] = useState(null);
  const [directMappingReviewMode, setDirectMappingReviewMode] = useState(false);
  const [joinMappingReviewMode, setJoinMappingReviewMode] = useState(false);
  const [aggregateMappingReviewMode, setAggregateMappingReviewMode] = useState(false);
  const [concatMappingReviewMode, setConcatMappingReviewMode] = useState(false);
  const [computeMappingReviewMode, setComputeMappingReviewMode] = useState(false);

  const closeDirectDialog = useCallback(() => {
    setDirectMappingDialogOpen(false);
    setDirectMappingInitialValues(null);
    setDirectMappingEditIndex(null);
    setDirectMappingReviewMode(false);
  }, []);
  const closeJoinDialog = useCallback(() => {
    setJoinDialogOpen(false);
    setJoinDialogInitialValues(null);
    setJoinMappingEditIndex(null);
    setJoinMappingReviewMode(false);
  }, []);
  const closeAggregateDialog = useCallback(() => {
    setAggregateMappingDialogOpen(false);
    setAggregateMappingInitialValues(null);
    setAggregateMappingEditIndex(null);
    setAggregateMappingReviewMode(false);
  }, []);
  const closeConcatDialog = useCallback(() => {
    setConcatMappingDialogOpen(false);
    setConcatMappingInitialValues(null);
    setConcatMappingEditIndex(null);
    setConcatMappingReviewMode(false);
  }, []);
  const closeComputeDialog = useCallback(() => {
    setComputeMappingDialogOpen(false);
    setComputeMappingInitialValues(null);
    setComputeMappingEditIndex(null);
    setComputeMappingReviewMode(false);
  }, []);

  const handleAddMapping = useCallback((sectionKey) => {
    if (sectionKey === 'direct') {
      setDirectMappingInitialValues(null);
      setDirectMappingEditIndex(null);
      setDirectMappingDialogOpen(true);
    }
    if (sectionKey === 'join') {
      setJoinDialogInitialValues(null);
      setJoinMappingEditIndex(null);
      setJoinDialogOpen(true);
    }
    if (sectionKey === 'aggregate') {
      setAggregateMappingDialogOpen(true);
      setAggregateMappingInitialValues(null);
    }
    if (sectionKey === 'concat') {
      setConcatMappingDialogOpen(true);
      setConcatMappingInitialValues(null);
    }
    if (sectionKey === 'compute') {
      setComputeMappingDialogOpen(true);
      setComputeMappingInitialValues(null);
    }
  }, []);

  const handleEditMapping = useCallback((mapping, editIndex) => {
    if (!mapping || typeof mapping !== 'object') return;
    const mappingType = (mapping.mapping_type || '').toUpperCase();
    const sourceCols = Array.isArray(mapping.source_columns) ? mapping.source_columns : [];
    const targetTable = mapping.destination_name ?? mapping.destinationName ?? '';
    const targetCol = mapping.target_column ?? mapping.targetColumn ?? '';

    if (mappingType === 'JOIN') {
      const source1 = sourceCols[0] != null ? parseTableCol(mapping.source_columns[0]) : { table: '', column: '' };
      const source2 = sourceCols[1] != null ? parseTableCol(mapping.source_columns[1]) : { table: '', column: '' };
      const groupByRawList = mapping.aggregate_group_by ?? (source1.column ? [source1.column] : []);
      const groupByCols = groupByRawList
        .map((g) => (typeof g === 'string' && g.includes('.') ? g.split('.')[1] : g))
        .filter(Boolean);
      const aggregateColRaw = mapping.aggregate_col ?? '';
      const aggregateColCol = aggregateColRaw.includes('.') ? aggregateColRaw.split('.')[1] : aggregateColRaw;
      setJoinDialogInitialValues({
        source1Table: source1.table,
        source1Col: source1.column,
        source2Table: source2.table,
        source2Col: source2.column,
        targetTable,
        targetCol,
        joinType: (mapping.join_type ?? 'LEFT').toUpperCase(),
        cardinality: mapping.cardinality ?? 'many-one',
        groupBy: groupByCols,
        operation: (mapping.aggregate_type ?? 'SUM').toUpperCase(),
        aggregateCol: aggregateColCol,
      });
      setJoinMappingEditIndex(editIndex);
      setJoinDialogOpen(true);
    } else if (mappingType === 'CONCAT') {
      handleEditConcatMapping(mapping, editIndex);
    } else if (mappingType === 'COMPUTE') {
      handleEditComputeMapping(mapping, editIndex);
    } else if (mappingType === 'AGGREGATE') {
      handleEditAggregateMapping(mapping, editIndex);
    } else {
      const src = sourceCols[0] != null ? parseTableCol(mapping.source_columns[0]) : { table: '', column: '' };
      setDirectMappingEditIndex(editIndex);
      setDirectMappingInitialValues({
        sourceTable: src.table,
        sourceCol: src.column,
        targetTable,
        targetCol,
      });
      setDirectMappingDialogOpen(true);
    }
  }, []);

  const handleReviewMapping = useCallback((mapping, editIndex) => {
    if (!mapping || typeof mapping !== 'object') return;
    const mappingType = (mapping.mapping_type || '').toUpperCase();
    const sourceCols = Array.isArray(mapping.source_columns) ? mapping.source_columns : [];
    const targetTable = mapping.destination_name ?? mapping.destinationName ?? '';
    const targetCol = mapping.target_column ?? mapping.targetColumn ?? '';

    if (mappingType === 'JOIN') {
      const source1 = sourceCols[0] != null ? parseTableCol(mapping.source_columns[0]) : { table: '', column: '' };
      const source2 = sourceCols[1] != null ? parseTableCol(mapping.source_columns[1]) : { table: '', column: '' };
      const groupByRawList = mapping.aggregate_group_by ?? (source1.column ? [source1.column] : []);
      const groupByCols = groupByRawList
        .map((g) => (typeof g === 'string' && g.includes('.') ? g.split('.')[1] : g))
        .filter(Boolean);
      const aggregateColRaw = mapping.aggregate_col ?? '';
      const aggregateColCol = aggregateColRaw.includes('.') ? aggregateColRaw.split('.')[1] : aggregateColRaw;
      setJoinDialogInitialValues({
        source1Table: source1.table,
        source1Col: source1.column,
        source2Table: source2.table,
        source2Col: source2.column,
        targetTable,
        targetCol,
        joinType: (mapping.join_type ?? 'LEFT').toUpperCase(),
        cardinality: mapping.cardinality ?? 'many-one',
        groupBy: groupByCols,
        operation: (mapping.aggregate_type ?? 'SUM').toUpperCase(),
        aggregateCol: aggregateColCol,
      });
      setJoinMappingEditIndex(editIndex);
      setJoinMappingReviewMode(true);
      setJoinDialogOpen(true);
    } else if (mappingType === 'CONCAT') {
      handleEditConcatMapping(mapping, editIndex);
      setConcatMappingReviewMode(true);
    } else if (mappingType === 'COMPUTE') {
      handleEditComputeMapping(mapping, editIndex);
      setComputeMappingReviewMode(true);
    } else if (mappingType === 'AGGREGATE') {
      handleEditAggregateMapping(mapping, editIndex);
      setAggregateMappingReviewMode(true);
    } else {
      const src = sourceCols[0] != null ? parseTableCol(mapping.source_columns[0]) : { table: '', column: '' };
      setDirectMappingEditIndex(editIndex);
      setDirectMappingInitialValues({
        sourceTable: src.table,
        sourceCol: src.column,
        targetTable,
        targetCol,
      });
      setDirectMappingReviewMode(true);
      setDirectMappingDialogOpen(true);
    }
  }, []);

  const handleEditAggregateMapping = useCallback((mapping, editIndex) => {
    if (!mapping || typeof mapping !== 'object') return;

    const sourceCols = Array.isArray(mapping.source_columns) ? mapping.source_columns : [];

    const src = sourceCols[0] != null ? parseTableCol(sourceCols[0]) : { table: '', column: '' };

    const targetTable = mapping.destination_name ?? mapping.destinationName ?? '';

    const targetCol = mapping.target_column ?? mapping.targetColumn ?? '';

    const groupByRawList = mapping.aggregate_group_by ?? [];
    const groupByColumns = groupByRawList
      .map((g) => (typeof g === 'string' && g.includes('.') ? g.split('.')[1] : g))
      .filter(Boolean);

    const aggregateColRaw = mapping.aggregate_col ?? '';
    const aggregateCol = aggregateColRaw.includes('.') ? aggregateColRaw.split('.')[1] : aggregateColRaw;

    setAggregateMappingInitialValues({
      sourceTable: src.table,
      groupByColumns,
      aggregateCol,
      aggregateType: (mapping.aggregate_type ?? 'SUM').toUpperCase(),
      targetTable,
      targetCol,
    });

    setAggregateMappingEditIndex(editIndex);
    setAggregateMappingDialogOpen(true);
  }, []);

  const handleEditConcatMapping = useCallback((mapping, editIndex) => {
    if (!mapping) return;

    const sourceCols = Array.isArray(mapping.source_columns) ? mapping.source_columns : [];

    if (!sourceCols.length) return;

    const firstParsed = parseTableCol(sourceCols[0]);

    const concatColumns = sourceCols
      .map((col) => {
        const parsed = parseTableCol(col);
        return parsed.column;
      })
      .filter(Boolean);

    const targetTable = mapping.destination_name ?? mapping.destinationName ?? '';

    const targetColumn = mapping.target_column ?? mapping.targetColumn ?? '';

    setConcatMappingEditIndex(editIndex);

    setConcatMappingInitialValues({
      sourceTable: firstParsed.table,
      concatColumns,
      delimiter: mapping.delimiter ?? ' ',
      destination_name: targetTable,
      target_column: targetColumn,

      confidence: mapping.confidence,
      mapping_state: mapping.mapping_state,
      possible_matches: mapping.possible_matches,
      reasoning: mapping.reasoning,
      id: mapping.id,
    });

    setConcatMappingDialogOpen(true);
  }, []);

  const handleEditComputeMapping = useCallback((mapping, editIndex) => {
    if (!mapping) return;

    const sourceCols = Array.isArray(mapping.source_columns) ? mapping.source_columns : [];

    if (!sourceCols.length) return;

    const firstParsed = parseTableCol(sourceCols[0]);
    const secondParsed = sourceCols[1] != null ? parseTableCol(sourceCols[1]) : { table: '', column: '' };

    const targetTable = mapping.destination_name ?? mapping.destinationName ?? '';

    const targetColumn = mapping.target_column ?? mapping.targetColumn ?? '';

    setComputeMappingEditIndex(editIndex);

    setComputeMappingInitialValues({
      sourceTable: firstParsed.table,
      primaryColumn: firstParsed.column,
      secondaryColumn: secondParsed.column || '',

      destination_name: targetTable,
      target_column: targetColumn,

      // preserve metadata
      confidence: mapping.confidence,
      mapping_state: mapping.mapping_state,
      possible_matches: mapping.possible_matches,
      reasoning: mapping.reasoning,
      id: mapping.id,
    });

    setComputeMappingDialogOpen(true);
  }, []);

  const onSaveConcatMapping = useCallback(
    (payload) => {
      setMappings((prev) => {
        const p = Array.isArray(prev) ? prev : [];

        const newMapping = {
          target_column: payload.targetColumn ?? '',
          mapping_type: 'CONCAT',
          source_columns: payload.source_columns ?? [],
          delimiter: payload.delimiter ?? ' ',
          confidence: payload.confidence ?? '',
          mapping_state: concatMappingEditIndex != null ? 'user_edited' : 'auto_merge',
          possible_matches: payload.possible_matches ?? [],
          reasoning: payload.reasoning ?? '',
          destination_name: payload.destinationName ?? payload.destination_name ?? '',
        };

        if (concatMappingEditIndex != null && concatMappingEditIndex >= 0 && concatMappingEditIndex < p.length) {
          return p.map((m, i) => (i === concatMappingEditIndex ? newMapping : m));
        }

        return [...p, newMapping];
      });

      setConcatMappingEditIndex(null);
    },
    [concatMappingEditIndex]
  );

  const onSaveAggregateMapping = useCallback(
    (payload) => {
      setMappings((prev) => {
        const p = Array.isArray(prev) ? prev : [];

        const newMapping = {
          mapping_type: 'AGGREGATE',

          source_columns: [
            `${payload.sourceTable}.${payload.aggregateCol}`,
            ...(payload.groupByColumns || []).map((col) => `${payload.sourceTable}.${col}`),
          ],

          aggregate_type: payload.aggregateType ?? '',
          aggregate_group_by: (payload.groupByColumns || []).map((col) => `${payload.sourceTable}.${col}`),
          aggregate_col: `${payload.sourceTable}.${payload.aggregateCol}`,

          target_column: payload.targetCol ?? '',
          destination_name: payload.targetTable ?? '',

          confidence: payload.confidence ?? 1,
          mapping_state: aggregateMappingEditIndex != null ? 'user_edited' : 'auto_merge',

          possible_matches: payload.possible_matches ?? payload.possibleMatches ?? [],

          reasoning: payload.reasoning ?? '',
          id: payload.id ?? undefined,
        };

        if (
          aggregateMappingEditIndex != null &&
          aggregateMappingEditIndex >= 0 &&
          aggregateMappingEditIndex < p.length
        ) {
          return p.map((m, i) => (i === aggregateMappingEditIndex ? newMapping : m));
        }
        return [...p, newMapping];
      });
      setAggregateMappingEditIndex(null);
    },
    [aggregateMappingEditIndex]
  );

  const onSaveComputeMapping = useCallback(
    (payload) => {
      setMappings((prev) => {
        const p = Array.isArray(prev) ? prev : [];

        const newMapping = {
          target_column: payload.targetColumn ?? '',
          mapping_type: 'COMPUTE',
          source_columns: payload.source_columns ?? [],
          left: payload.left ?? '',
          right: payload.right ?? '',
          leftKey: payload.leftKey ?? '',
          rightKey: payload.rightKey ?? '',
          primaryColumn: payload.primaryColumn ?? '',
          secondaryColumn: payload.secondaryColumn ?? null,
          confidence: payload.confidence ?? '',
          mapping_state: computeMappingEditIndex != null ? 'user_edited' : 'auto_merge',
          possible_matches: payload.possible_matches ?? payload.possibleMatches ?? [],
          reasoning: payload.reasoning ?? '',
          destination_name: payload.destinationName ?? payload.destination_name ?? '',
          id: payload.id ?? undefined,
        };

        if (computeMappingEditIndex != null && computeMappingEditIndex >= 0 && computeMappingEditIndex < p.length) {
          return p.map((m, i) => (i === computeMappingEditIndex ? newMapping : m));
        }

        return [...p, newMapping];
      });

      setComputeMappingEditIndex(null);
    },
    [computeMappingEditIndex]
  );

  return (
    <Box
      sx={{
        flex: 1,
        overflow: 'auto',
        p: 3,
        backgroundColor: '#f5f5f5',
        minHeight: 0,
      }}
    >
      <Grid container spacing={2} sx={{ height: '100%' }}>
        <Grid item xs={12} sx={{ height: 'calc(100% - 80px)' }}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              height: '100%',
              '& > *': {
                flex: '1 1 auto',
                height: '100%',
                minWidth: {
                  xs: '100%',
                  md: '300px',
                },
              },
            }}
          >
            <>
              <SourcePanel
                sources={sources}
                onSourcesChange={onSourcesChange}
                disabled={disableForm}
                duckDBReady={isInitialized}
                sourceProfileData={sourceProfileData}
              />

              <TargetPanel
                targetSchema={targetSchema}
                targetProfileData={targetProfileData}
                mappings={mappings}
                onMappingsChange={onMappingsChange}
                disabled={disableForm}
                targetHeaders={fetchedHeader.filter((h) => h.sourceTable === targetSchema)}
                duckDBReady={isInitialized}
              />

              <MiddlePanel
                mappings={mappings}
                setMappings={setMappings}
                aiSuggestions={aiSuggestions}
                onJoinsChange={onJoinsChange}
                onAiSuggestionsChange={onAiSuggestionsChange}
                onRequestAI={onRequestAI}
                disabled={disableForm || mappingLoading}
                sources={sources}
                duckDBReady={isInitialized}
                mappingLoading={mappingLoading}
                totalTargetColumns={totalTargetColumns}
                onAddMapping={handleAddMapping}
                onEditMapping={handleEditMapping}
                onReviewMapping={handleReviewMapping}
                onDeleteMapping={onDeleteMapping}
                onPreview={onPreview}
                isPreviewReady={isPreviewReady}
                onSaveMappings={onSaveMappings}
                onRegenerateMappings={onRegenerateMappings}
                onOpenPreviewDialog={onOpenPreviewDialog}
              />

              <DirectMappingDialog
                open={directMappingDialogOpen}
                onClose={closeDirectDialog}
                sources={sources}
                targetSchema={targetSchema}
                fetchedHeader={fetchedHeader || []}
                initialValues={directMappingInitialValues}
                editIndex={directMappingEditIndex}
                existingMappings={existingMappings}
                reviewMode={directMappingReviewMode}
                onSave={(payload, editIndexFromDialog) => {
                  onSaveDirectMapping?.(payload, editIndexFromDialog ?? directMappingEditIndex);
                }}
                onReject={
                  directMappingReviewMode
                    ? () => {
                        setMappings((prev) => prev.filter((_, i) => i !== directMappingEditIndex));
                        closeDirectDialog();
                      }
                    : undefined
                }
                onApprove={
                  directMappingReviewMode
                    ? () => {
                        setMappings((prev) =>
                          prev.map((m, i) =>
                            i === directMappingEditIndex ? { ...m, mapping_state: 'auto_merge' } : m
                          )
                        );
                        closeDirectDialog();
                      }
                    : undefined
                }
              />
              <JoinWithAggregateDialog
                open={joinDialogOpen}
                onClose={closeJoinDialog}
                sources={sources}
                targetSchema={targetSchema}
                fetchedHeader={fetchedHeader || []}
                initialValues={joinDialogInitialValues}
                existingMappings={existingMappings}
                isEdit={joinMappingEditIndex != null}
                reviewMode={joinMappingReviewMode}
                onSave={(payload) => {
                  onSaveJoinMapping?.(payload, joinMappingEditIndex);
                }}
                onReject={
                  joinMappingReviewMode
                    ? () => {
                        setMappings((prev) => prev.filter((_, i) => i !== joinMappingEditIndex));
                        closeJoinDialog();
                      }
                    : undefined
                }
                onApprove={
                  joinMappingReviewMode
                    ? () => {
                        setMappings((prev) =>
                          prev.map((m, i) =>
                            i === joinMappingEditIndex ? { ...m, mapping_state: 'auto_merge' } : m
                          )
                        );
                        closeJoinDialog();
                      }
                    : undefined
                }
              />
              <AggregateMappingDialog
                open={aggregateMappingDialogOpen}
                onClose={closeAggregateDialog}
                sources={sources}
                targetSchema={targetSchema}
                fetchedHeader={fetchedHeader || []}
                initialValues={aggregateMappingInitialValues}
                existingMappings={existingMappings}
                reviewMode={aggregateMappingReviewMode}
                onSave={onSaveAggregateMapping}
                onReject={
                  aggregateMappingReviewMode
                    ? () => {
                        setMappings((prev) => prev.filter((_, i) => i !== aggregateMappingEditIndex));
                        closeAggregateDialog();
                      }
                    : undefined
                }
                onApprove={
                  aggregateMappingReviewMode
                    ? () => {
                        setMappings((prev) =>
                          prev.map((m, i) =>
                            i === aggregateMappingEditIndex ? { ...m, mapping_state: 'auto_merge' } : m
                          )
                        );
                        closeAggregateDialog();
                      }
                    : undefined
                }
              />
              <ConcatMappingDialog
                open={concatMappingDialogOpen}
                onClose={closeConcatDialog}
                sources={sources}
                targetSchema={targetSchema}
                fetchedHeader={fetchedHeader || []}
                initialValues={concatMappingInitialValues}
                existingMappings={existingMappings}
                reviewMode={concatMappingReviewMode}
                onSave={onSaveConcatMapping}
                onReject={
                  concatMappingReviewMode
                    ? () => {
                        setMappings((prev) => prev.filter((_, i) => i !== concatMappingEditIndex));
                        closeConcatDialog();
                      }
                    : undefined
                }
                onApprove={
                  concatMappingReviewMode
                    ? () => {
                        setMappings((prev) =>
                          prev.map((m, i) =>
                            i === concatMappingEditIndex ? { ...m, mapping_state: 'auto_merge' } : m
                          )
                        );
                        closeConcatDialog();
                      }
                    : undefined
                }
              />
              <ComputeMappingDialog
                open={computeMappingDialogOpen}
                onClose={closeComputeDialog}
                sources={sources}
                targetSchema={targetSchema}
                fetchedHeader={fetchedHeader || []}
                initialValues={computeMappingInitialValues}
                existingMappings={existingMappings}
                reviewMode={computeMappingReviewMode}
                onSave={onSaveComputeMapping}
                onReject={
                  computeMappingReviewMode
                    ? () => {
                        setMappings((prev) => prev.filter((_, i) => i !== computeMappingEditIndex));
                        closeComputeDialog();
                      }
                    : undefined
                }
                onApprove={
                  computeMappingReviewMode
                    ? () => {
                        setMappings((prev) =>
                          prev.map((m, i) =>
                            i === computeMappingEditIndex ? { ...m, mapping_state: 'auto_merge' } : m
                          )
                        );
                        closeComputeDialog();
                      }
                    : undefined
                }
              />
            </>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ModalContent;
